import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { Button, Field, PageSpinner } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { keys } from "@/lib/queries";

function AuthCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex size-11 items-center justify-center rounded-xl bg-accent text-white">
            <svg viewBox="0 0 100 100" className="size-7">
              <path d="M22 30h56M22 50h38M22 70h48" stroke="currentColor" strokeWidth="10" strokeLinecap="round" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
        </div>
        <div className="rounded-xl border border-line bg-surface p-6">{children}</div>
      </div>
    </div>
  );
}

function safeNext(next: string | null) {
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const setup = useQuery({ queryKey: keys.setup, queryFn: api.setupStatus });

  if (setup.data?.needsSetup) return <Navigate to="/setup" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await api.login({ email, password });
      qc.setQueryData(keys.me, user);
      navigate(safeNext(params.get("next")), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Welcome back" subtitle="Sign in to your team's workspace">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email">
          <input type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className="field" />
        </Field>
        <Field label="Password">
          <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="field" />
        </Field>
        {error && <p className="text-sm text-danger">{error}</p>}
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Sign in
        </Button>
        <p className="text-center text-xs text-faint">Accounts are created by your administrator.</p>
      </form>
    </AuthCard>
  );
}

export function SetupPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setup = useQuery({ queryKey: keys.setup, queryFn: api.setupStatus });
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (setup.isPending) return <PageSpinner />;
  if (setup.data && !setup.data.needsSetup) return <Navigate to="/login" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await api.setup(form);
      qc.setQueryData(keys.me, user);
      qc.setQueryData(keys.setup, { needsSetup: false });
      navigate("/", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title="Set up your workspace" subtitle="Create the first administrator account">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full name">
          <input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field" dir="auto" />
        </Field>
        <Field label="Email">
          <input type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field" />
        </Field>
        <Field label="Password" hint="At least 8 characters">
          <input type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field" />
        </Field>
        {error && <p className="text-sm text-danger">{error}</p>}
        <Button type="submit" variant="primary" className="w-full" loading={busy}>
          Create admin account
        </Button>
      </form>
    </AuthCard>
  );
}
